/**
 * Reglas de negocio puras para Eventos.
 */

import type { Evento } from "@app-types/entities";
import { type TipoEvento, TIPOS_EVENTO } from "@constants/catalogos";

/**
 * Genera el código humano de un evento usando el padre (SO o AFE) y un
 * consecutivo por padre. Formato: `EV-{parentCodigo}-{NN}` (NN con dos
 * dígitos como mínimo).
 *
 *   EV-SO-094-001-01  → primer evento de la SO-094-001
 *   EV-AFE-CTFA-26-001-03 → tercer evento del AFE CTFA-26-001
 *
 * Si el evento no está ligado a SO ni AFE (caso raro), se cae a "GEN" como
 * etiqueta de fallback.
 */
export function generarCodigoEvento({
  afeCodigo,
  soCodigo,
  codigosExistentes,
}: {
  afeCodigo?: string;
  soCodigo?: string;
  /** Códigos ya asignados a otros eventos (del tenant). Usado para evitar duplicados. */
  codigosExistentes: (string | undefined)[];
}): string {
  // Prioridad: SO es más específica que AFE; si solo hay AFE, se usa AFE.
  const parent = soCodigo?.trim() || afeCodigo?.trim() || "GEN";
  const prefix = `EV-${parent}-`;
  const numeros = codigosExistentes
    .filter((c): c is string => !!c && c.startsWith(prefix))
    .map((c) => Number(c.slice(prefix.length)))
    .filter((n) => Number.isFinite(n));
  const next = (numeros.length > 0 ? Math.max(...numeros) : 0) + 1;
  return `${prefix}${String(next).padStart(2, "0")}`;
}

const TIPOS_QUE_REQUIEREN_HSE = new Set<TipoEvento>(["HSE", "SEG_FISICA"]);
const TIPOS_AMBIENTALES = new Set<TipoEvento>(["AMBIENTAL"]);

export function esEventoCritico(evento: Pick<Evento, "tipo">): boolean {
  return TIPOS_QUE_REQUIEREN_HSE.has(evento.tipo as TipoEvento);
}

export function esEventoAmbiental(evento: Pick<Evento, "tipo">): boolean {
  return TIPOS_AMBIENTALES.has(evento.tipo as TipoEvento);
}

export function validarTipo(tipo: string): { valido: boolean; mensaje?: string } {
  if (!TIPOS_EVENTO.includes(tipo as TipoEvento)) {
    return { valido: false, mensaje: `Tipo de evento "${tipo}" no es valido` };
  }
  return { valido: true };
}

export function validarEvento(
  evento: Pick<Evento, "tipo" | "descripcion" | "fecha" | "soId">,
): { valido: boolean; errores: string[] } {
  const errores: string[] = [];
  const t = validarTipo(evento.tipo);
  if (!t.valido && t.mensaje) errores.push(t.mensaje);
  if (!evento.descripcion || evento.descripcion.trim().length < 5) {
    errores.push("Descripcion debe tener al menos 5 caracteres");
  }
  if (!evento.fecha) {
    errores.push("Fecha es obligatoria");
  }
  if (!evento.soId) {
    errores.push("Orden de Trabajo asociada es obligatoria");
  }
  return { valido: errores.length === 0, errores };
}

/** Cuenta eventos por tipo. */
export function contarPorTipo(eventos: Evento[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const t of TIPOS_EVENTO) counts[t] = 0;
  for (const e of eventos) {
    counts[e.tipo] = (counts[e.tipo] ?? 0) + 1;
  }
  return counts;
}

/** Filtra eventos del ultimo N dias. */
export function eventosRecientes(eventos: Evento[], dias = 7, hoy = new Date()): Evento[] {
  const limite = new Date(hoy);
  limite.setDate(limite.getDate() - dias);
  const limiteIso = limite.toISOString().slice(0, 10);
  return eventos.filter((e) => e.fecha >= limiteIso);
}
